import { useState, useEffect, useCallback } from 'react';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn as amplifySignIn,
  signInWithRedirect,
  signOut as amplifySignOut,
} from 'aws-amplify/auth';

import { configureAmplify } from './amplify';

export type AuthState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  | { status: 'authenticated'; email: string; groups: string[] }
  | { status: 'error'; message: string };

export interface UseAuth {
  state: AuthState;
  /** SRP email/password (or mock). In Hosted-UI mode this redirects instead. */
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  /** Re-run the session bootstrap (used by the error-state retry button). */
  retry: () => void;
  /** True when running against the real Cognito pool (vs mock). */
  isReal: boolean;
  /** True when sign-in is Cognito Hosted UI (redirect) rather than an in-app form. */
  hostedUi: boolean;
}

const MOCK_ENABLED = import.meta.env.VITE_MOCK_AUTH === 'true';

// Configure Amplify once at module load. When env is absent, mock mode
// drives the whole flow.
const CONFIG = MOCK_ENABLED
  ? { configured: false, hostedUi: false }
  : configureAmplify();
const REAL_CONFIGURED = CONFIG.configured;
const HOSTED_UI = CONFIG.hostedUi;

function parseMockGroups(): string[] {
  const raw = import.meta.env.VITE_MOCK_GROUPS as string | undefined;
  if (raw == null) return ['atlas', 'preflight', 'processlab', 'sourcing'];
  return raw
    .split(',')
    .map((g) => g.trim())
    .filter(Boolean);
}

/**
 * Read product-access groups from the ID token, resilient to which claim
 * the pool emits. The per-product-groups model (PR #193) puts product
 * grants in `cognito:groups`; the original pool also carries
 * `custom:roles` (comma-separated). We union both so a contract drift on
 * the backend never silently hides a user's tiles.
 */
function extractGroups(payload: Record<string, unknown>): string[] {
  const out = new Set<string>();
  const groups = payload['cognito:groups'];
  if (Array.isArray(groups)) {
    for (const g of groups) if (typeof g === 'string') out.add(g);
  }
  const roles = payload['custom:roles'];
  if (typeof roles === 'string') {
    for (const r of roles.split(',')) {
      const t = r.trim();
      if (t) out.add(t);
    }
  }
  return [...out];
}

/** Dev-only state preview: `?state=loading|error|noaccess` in mock mode. */
function mockStateOverride(): 'loading' | 'error' | 'noaccess' | null {
  if (!MOCK_ENABLED || typeof window === 'undefined') return null;
  const s = new URLSearchParams(window.location.search).get('state');
  return s === 'loading' || s === 'error' || s === 'noaccess' ? s : null;
}

async function readRealSession(): Promise<AuthState> {
  try {
    await getCurrentUser();
  } catch {
    return { status: 'unauthenticated' };
  }
  const session = await fetchAuthSession();
  const payload = session.tokens?.idToken?.payload;
  if (!payload) return { status: 'unauthenticated' };
  const email = (payload['email'] as string | undefined) ?? '';
  return {
    status: 'authenticated',
    email,
    groups: extractGroups(payload as Record<string, unknown>),
  };
}

export function useAuth(): UseAuth {
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;

    if (MOCK_ENABLED) {
      const override = mockStateOverride();
      if (override === 'loading') return; // stay in loading forever
      if (override === 'error') {
        setState({ status: 'error', message: 'Mock error state preview.' });
        return;
      }
      setState({
        status: 'authenticated',
        email: 'demo@fololabs.io',
        groups: override === 'noaccess' ? [] : parseMockGroups(),
      });
      return;
    }

    // Production safety: real mode but Cognito env not configured (e.g.
    // params not yet set in CF Pages). Fail loud rather than silently
    // dropping to a broken login.
    if (!REAL_CONFIGURED) {
      setState({
        status: 'error',
        message:
          'Sign-in is not configured for this environment yet. Please check back soon.',
      });
      return;
    }

    setState({ status: 'loading' });
    readRealSession()
      .then((s) => {
        if (!cancelled) setState(s);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setState({
            status: 'error',
            message:
              e instanceof Error ? e.message : 'Could not reach sign-in.',
          });
      });

    return () => {
      cancelled = true;
    };
  }, [nonce]);

  const signIn = useCallback(async (email: string, password: string) => {
    if (MOCK_ENABLED) {
      setState({
        status: 'authenticated',
        email: email || 'demo@fololabs.io',
        groups: parseMockGroups(),
      });
      return;
    }
    // Hosted UI — redirect to Cognito; the form's email/password are ignored.
    if (HOSTED_UI) {
      await signInWithRedirect();
      return;
    }
    // SRP — the only flow the pool client allows. Throw on challenge so
    // the login form can surface it; Amplify errors bubble to the caller.
    const result = await amplifySignIn({
      username: email,
      password,
      options: { authFlowType: 'USER_SRP_AUTH' },
    });
    if (result.isSignedIn) {
      setState(await readRealSession());
    } else {
      throw new Error(
        `Additional step required: ${result.nextStep.signInStep}.`,
      );
    }
  }, []);

  const signOut = useCallback(async () => {
    if (MOCK_ENABLED) {
      setState({ status: 'unauthenticated' });
      return;
    }
    await amplifySignOut();
    setState({ status: 'unauthenticated' });
  }, []);

  const retry = useCallback(() => setNonce((n) => n + 1), []);

  return {
    state,
    signIn,
    signOut,
    retry,
    isReal: REAL_CONFIGURED,
    hostedUi: HOSTED_UI,
  };
}
